import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Phone,
  Mail,
  Clock,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import shifaLogo from "@/assets/images/shifa-logo.png";
import { siteConfig } from "@/data/site";
import { departments } from "@/data/departments";
import { services } from "@/data/services";

type NavDropdown = {
  title: string;
  allLabel: string;
  items: { id: string; name: string; href: string }[];
  /** Tailwind classes controlling the desktop mega-menu size/columns */
  panelClass: string;
  gridClass: string;
};

type NavLink = { label: string; href: string; dropdown?: NavDropdown };

const navLinks: NavLink[] = [
  { label: "Home", href: "/" },
  {
    label: "Departments",
    href: "/departments",
    dropdown: {
      title: "Our Departments",
      allLabel: "All Departments",
      items: departments.map((d) => ({
        id: d.id,
        name: d.name,
        href: `/departments/${d.id}`,
      })),
      panelClass: "w-[640px]",
      gridClass: "grid-cols-3",
    },
  },
  {
    label: "Services",
    href: "/services",
    dropdown: {
      title: "Our Services",
      allLabel: "All Services",
      items: services.map((s) => ({
        id: s.id,
        name: s.name,
        href: `/services/${s.id}`,
      })),
      panelClass: "w-[420px]",
      gridClass: "grid-cols-2",
    },
  },
  { label: "Doctors", href: "/doctors" },
  { label: "Exceptional Cases", href: "/rare-cases" },
  { label: "Contact", href: "/contact" },
  { label: "About", href: "/about" },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(
    null,
  );
  const [scrolled, setScrolled] = useState(false);
  const desktopNavRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileOpenDropdown(null);
  }, [location.pathname]);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        desktopNavRef.current &&
        !desktopNavRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleDropdownEnter = useCallback((label: string) => {
    clearTimeout(dropdownTimeoutRef.current);
    setOpenDropdown(label);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    dropdownTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 200);
  }, []);

  const scrollToContactNumbers = useCallback((e: ReactMouseEvent) => {
    const target = document.getElementById("contact-numbers");
    if (!target) return; // fall back to the hash link
    e.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Top info bar */}
      <div className="hidden bg-primary-900 text-white lg:block">
        <div className="container-app flex items-center justify-between py-1.5 text-xs">
          <div className="flex items-center gap-6">
            <a
              href="#contact-numbers"
              onClick={scrollToContactNumbers}
              className="flex items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <Phone className="h-3 w-3" />
              <span>Call Us</span>
            </a>
            <a
              href={`mailto:${siteConfig.contact.email}`}
              className="flex items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <Mail className="h-3 w-3" />
              <span>{siteConfig.contact.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            <span>Mon - Sat: Consultation | Sun: Emergency Only</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className={cn(
          "border-b border-gray-100 bg-white/80 backdrop-blur-lg transition-shadow duration-300",
          scrolled && "shadow-sm shadow-gray-200/60",
        )}
      >
        <div className="container-app flex h-16 items-center justify-between lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={shifaLogo} alt="Shifa Medical Centre" className="h-12 w-auto" />
          </Link>

          {/* Desktop nav links */}
          <div ref={desktopNavRef} className="hidden items-center gap-1 lg:flex">
            {navLinks.map((link) => {
              const dropdown = link.dropdown;
              if (!dropdown) {
                return (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={cn(
                      "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive(link.href)
                        ? "text-primary-600"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                    )}
                  >
                    {link.label}
                  </Link>
                );
              }

              const isOpen = openDropdown === link.label;

              return (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter(link.label)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button
                    onClick={() =>
                      setOpenDropdown((p) => (p === link.label ? null : link.label))
                    }
                    className={cn(
                      "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive(link.href)
                        ? "text-primary-600"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                    )}
                  >
                    {link.label}
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 transition-transform duration-200",
                        isOpen && "rotate-180",
                      )}
                    />
                  </button>

                  {/* Mega menu */}
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                        className="absolute left-1/2 top-full pt-2 -translate-x-1/2"
                      >
                        <div
                          className={cn(
                            "rounded-xl border border-gray-100 bg-white p-5 shadow-xl shadow-gray-200/50",
                            dropdown.panelClass,
                          )}
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">
                              {dropdown.title}
                            </h3>
                            <Link
                              to={link.href}
                              className="text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
                            >
                              View all &rarr;
                            </Link>
                          </div>
                          <div className={cn("grid gap-1", dropdown.gridClass)}>
                            {dropdown.items.map((item) => (
                              <Link
                                key={item.id}
                                to={item.href}
                                className="rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Desktop CTA buttons */}
          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={`tel:${siteConfig.contact.emergency.replace(/-/g, "")}`}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              Emergency
            </a>
            <Link
              to="/appointment"
              className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 active:bg-primary-800"
            >
              Book Appointment
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay + drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col bg-white shadow-2xl"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <Link
                  to="/"
                  className="flex items-center"
                  onClick={() => setMobileOpen(false)}
                >
                  <img src={shifaLogo} alt="Shifa Medical Centre" className="h-10 w-auto" />
                </Link>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Drawer links */}
              <nav className="flex-1 overflow-y-auto px-5 py-4">
                <ul className="space-y-1">
                  {navLinks.map((link) => {
                    const dropdown = link.dropdown;
                    if (!dropdown) {
                      return (
                        <li key={link.label}>
                          <Link
                            to={link.href}
                            className={cn(
                              "block rounded-lg px-3 py-3 text-base font-medium transition-colors",
                              isActive(link.href)
                                ? "bg-primary-50 text-primary-700"
                                : "text-gray-700 hover:bg-gray-50",
                            )}
                          >
                            {link.label}
                          </Link>
                        </li>
                      );
                    }

                    const isOpen = mobileOpenDropdown === link.label;

                    return (
                      <li key={link.label}>
                        <button
                          onClick={() =>
                            setMobileOpenDropdown((p) =>
                              p === link.label ? null : link.label,
                            )
                          }
                          className={cn(
                            "flex w-full items-center justify-between rounded-lg px-3 py-3 text-base font-medium transition-colors",
                            isActive(link.href)
                              ? "bg-primary-50 text-primary-700"
                              : "text-gray-700 hover:bg-gray-50",
                          )}
                        >
                          {link.label}
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              isOpen && "rotate-180",
                            )}
                          />
                        </button>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <li>
                                <Link
                                  to={link.href}
                                  className="block rounded-lg px-6 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                                >
                                  {dropdown.allLabel} &rarr;
                                </Link>
                              </li>
                              {dropdown.items.map((item) => (
                                <li key={item.id}>
                                  <Link
                                    to={item.href}
                                    className="block rounded-lg px-6 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                                  >
                                    {item.name}
                                  </Link>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              {/* Drawer footer */}
              <div className="space-y-3 border-t border-gray-100 px-5 py-4">
                <Link
                  to="/appointment"
                  className="flex w-full items-center justify-center rounded-xl bg-primary-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                >
                  Book Appointment
                </Link>
                <a
                  href={`tel:${siteConfig.contact.emergency.replace(/-/g, "")}`}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 py-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100"
                >
                  <Phone className="h-4 w-4" />
                  Emergency: {siteConfig.contact.emergency}
                </a>
                <div className="flex items-center gap-4 pt-2 text-xs text-gray-500">
                  <a
                    href={`tel:${siteConfig.contact.phone.replace(/-/g, "")}`}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    <Phone className="h-3 w-3" />
                    {siteConfig.contact.phone}
                  </a>
                  <a
                    href={`mailto:${siteConfig.contact.email}`}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    <Mail className="h-3 w-3" />
                    Email
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
